import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import AvailabilityGrid from './components/AvailabilityGrid';
import AvailabilitySidebar from './components/AvailabilitySidebar';
import WeekNavigator from './components/WeekNavigator';
import ConflictModal from './components/ConflictModal';
import VacationRequestModal from './components/VacationRequestModal';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const PersonalAvailabilityManagement = () => {
  // Get current week start (Monday)
  const getCurrentWeekStart = () => {
    const today = new Date();
    const dayOfWeek = today?.getDay();
    const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const monday = new Date(today);
    monday?.setDate(today?.getDate() + mondayOffset);
    monday?.setHours(0, 0, 0, 0);
    return monday;
  };

  const [selectedWeek, setSelectedWeek] = useState(getCurrentWeekStart());
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [showVacationModal, setShowVacationModal] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Mock availability data - in real app this would come from API
  const [availability, setAvailability] = useState({
    'Montag-9': 'available',
    'Montag-10': 'available',
    'Montag-11': 'available',
    'Montag-14': 'preferred',
    'Montag-15': 'preferred',
    'Dienstag-8': 'available',
    'Dienstag-9': 'available',
    'Dienstag-10': 'available',
    'Mittwoch-9': 'conditional',
    'Mittwoch-10': 'conditional',
    'Donnerstag-14': 'available',
    'Donnerstag-15': 'available',
    'Donnerstag-16': 'available',
    'Freitag-9': 'preferred',
    'Freitag-10': 'preferred',
    'Samstag-10': 'unavailable',
    'Samstag-11': 'unavailable',
    'Sonntag-10': 'unavailable',
    'Sonntag-11': 'unavailable'
  });

  // Mock conflicting shifts data
  const conflictingShifts = [
    {
      day: 'Montag',
      startHour: 10,
      endHour: 12,
      shiftId: 'shift-001',
      type: 'confirmed_shift',
      description: 'Bestätigte Schicht: Kundenservice'
    },
    {
      day: 'Mittwoch',
      startHour: 9,
      endHour: 11,
      shiftId: 'shift-002',
      type: 'pending_shift',
      description: 'Ausstehende Schicht: Lager'
    }
  ];

  // Mock availability statistics
  const availabilityStats = {
    availableHours: 45,
    unavailableHours: 123,
    preferredHours: 12,
    conditionalHours: 8
  };

  // Mock upcoming impact data
  const upcomingImpact = [
    {
      date: '15.10.2025',
      level: 'high',
      description: 'Bestätigte Schicht könnte betroffen sein',
      suggestion: 'Vorgesetzte benachrichtigen'
    },
    {
      date: '18.10.2025',
      level: 'medium',
      description: 'Ausstehende Schichtanfrage',
      suggestion: 'Automatische Umplanung möglich'
    }
  ];

  // Mock saved patterns
  const [savedPatterns, setSavedPatterns] = useState([
    { name: 'Mein Standardmuster', data: 'weekdays_9_17' },
    { name: 'Wochenendschichten', data: 'weekends_full' }
  ]);

  // Mock vacation requests
  const [vacationRequests, setVacationRequests] = useState([
    {
      startDate: '2025-10-20',
      endDate: '2025-10-25',
      type: 'vacation',
      status: 'approved',
      reason: 'Familienurlaub'
    },
    {
      startDate: '2025-11-15',
      endDate: '2025-11-16',
      type: 'personal',
      status: 'pending',
      reason: 'Persönliche Angelegenheiten'
    }
  ]);

  // Mock conflicts for modal
  const [currentConflicts, setCurrentConflicts] = useState([]);

  const handleAvailabilityChange = (updates) => {
    setAvailability(prev => ({
      ...prev,
      ...updates
    }));
    setHasUnsavedChanges(true);
    
    // Check for conflicts
    const conflicts = checkForConflicts(updates);
    if (conflicts?.length > 0) {
      setCurrentConflicts(conflicts);
      setShowConflictModal(true);
    }
  };

  const checkForConflicts = (updates) => {
    const conflicts = [];
    
    Object.entries(updates)?.forEach(([cellKey, newAvailability]) => {
      const [day, hour] = cellKey?.split('-');
      const hourNum = parseInt(hour);
      
      // Check against conflicting shifts
      const conflict = conflictingShifts?.find(shift => 
        shift?.day === day && shift?.startHour <= hourNum && shift?.endHour > hourNum
      );
      
      if (conflict && newAvailability === 'unavailable') {
        conflicts?.push({
          day,
          startHour: conflict?.startHour,
          endHour: conflict?.endHour,
          type: conflict?.type,
          description: conflict?.description,
          currentAvailability: availability?.[cellKey] || 'unavailable',
          newAvailability,
          suggestions: [
            'Schicht an anderen Mitarbeiter übertragen',
            'Verfügbarkeit als "bedingt" markieren',
            'Vorgesetzten über Änderung informieren'
          ]
        });
      }
    });
    
    return conflicts;
  };

  const handlePresetApply = (pattern) => {
    const newAvailability = generatePatternAvailability(pattern);
    setAvailability(prev => ({
      ...prev,
      ...newAvailability
    }));
    setHasUnsavedChanges(true);
  };

  const generatePatternAvailability = (pattern) => {
    const newAvailability = {};
    const days = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];
    
    switch (pattern) {
      case 'weekdays_9_17':
        days?.slice(0, 5)?.forEach(day => {
          for (let hour = 9; hour < 17; hour++) {
            newAvailability[`${day}-${hour}`] = 'available';
          }
        });
        break;
      case 'weekends_full':
        days?.slice(5)?.forEach(day => {
          for (let hour = 0; hour < 24; hour++) {
            newAvailability[`${day}-${hour}`] = 'available';
          }
        });
        break;
      case 'weekdays_6_14':
        days?.slice(0, 5)?.forEach(day => {
          for (let hour = 6; hour < 14; hour++) {
            newAvailability[`${day}-${hour}`] = 'available';
          }
        });
        break;
      case 'weekdays_14_22':
        days?.slice(0, 5)?.forEach(day => {
          for (let hour = 14; hour < 22; hour++) {
            newAvailability[`${day}-${hour}`] = 'available';
          }
        });
        break;
      case 'weekdays_22_6':
        days?.slice(0, 5)?.forEach(day => {
          for (let hour = 22; hour < 24; hour++) {
            newAvailability[`${day}-${hour}`] = 'available';
          }
          for (let hour = 0; hour < 6; hour++) {
            newAvailability[`${day}-${hour}`] = 'available';
          }
        });
        break;
      case 'all_days_8_20':
        days?.forEach(day => {
          for (let hour = 8; hour < 20; hour++) {
            newAvailability[`${day}-${hour}`] = 'available';
          }
        });
        break;
    }
    
    return newAvailability;
  };

  const handleBulkUpdate = (operation) => {
    switch (operation) {
      case 'copy':
        // In real app, this would open a week selection modal
        console.log('Copy week functionality');
        break;
      case 'reset':
        setAvailability({});
        setHasUnsavedChanges(true);
        break;
      case 'export':
        exportAvailability();
        break;
    }
  };

  const exportAvailability = () => {
    const exportData = {
      week: selectedWeek?.toISOString(),
      availability,
      stats: availabilityStats
    };
    
    const blob = new Blob([JSON.stringify(exportData, null, 2)], {
      type: 'application/json'
    });
    
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `verfuegbarkeit-${selectedWeek?.toISOString()?.split('T')?.[0]}.json`;
    document.body?.appendChild(a);
    a?.click();
    document.body?.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handlePatternSave = (name) => {
    const newPattern = {
      name,
      data: availability
    };
    setSavedPatterns(prev => [...prev, newPattern]);
  };

  const handleWeekChange = (newWeek) => {
    if (hasUnsavedChanges) {
      if (window.confirm('Sie haben ungespeicherte Änderungen. Möchten Sie fortfahren?')) {
        setSelectedWeek(newWeek);
        setHasUnsavedChanges(false);
      }
    } else {
      setSelectedWeek(newWeek);
    }
  };

  const handleTodayClick = () => {
    setSelectedWeek(getCurrentWeekStart());
  };

  const handleResolveConflict = (conflict, resolution) => {
    console.log('Resolving conflict:', conflict, 'with resolution:', resolution);
    // In real app, this would handle the conflict resolution
    setCurrentConflicts(prev => prev?.filter(c => c !== conflict));
    
    if (currentConflicts?.length <= 1) {
      setShowConflictModal(false);
    }
  };

  const handleIgnoreConflict = (conflict) => {
    setCurrentConflicts(prev => prev?.filter(c => c !== conflict));
    
    if (currentConflicts?.length <= 1) {
      setShowConflictModal(false);
    }
  };

  const handleVacationSubmit = (vacationData) => {
    const newRequest = {
      ...vacationData,
      status: 'pending',
      submittedAt: new Date()?.toISOString()
    };
    
    setVacationRequests(prev => [...prev, newRequest]);
    
    // Update availability for vacation period
    const start = new Date(vacationData.startDate);
    const end = new Date(vacationData.endDate);
    const updates = {};
    
    for (let d = new Date(start); d <= end; d?.setDate(d?.getDate() + 1)) {
      const dayName = d?.toLocaleDateString('de-DE', { weekday: 'long' });
      const dayNameGerman = {
        'Monday': 'Montag',
        'Tuesday': 'Dienstag', 
        'Wednesday': 'Mittwoch',
        'Thursday': 'Donnerstag',
        'Friday': 'Freitag',
        'Saturday': 'Samstag',
        'Sunday': 'Sonntag'
      }?.[d?.toLocaleDateString('en-US', { weekday: 'long' })];
      
      for (let hour = 0; hour < 24; hour++) {
        updates[`${dayNameGerman}-${hour}`] = 'unavailable';
      }
    }
    
    setAvailability(prev => ({
      ...prev,
      ...updates
    }));
    setHasUnsavedChanges(true);
  };

  const handleSaveChanges = () => {
    // In real app, this would save to API
    console.log('Saving availability changes:', availability);
    setHasUnsavedChanges(false);
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event?.ctrlKey || event?.metaKey) {
        switch (event?.key) {
          case 's':
            event?.preventDefault();
            if (hasUnsavedChanges) {
              handleSaveChanges();
            }
            break;
          case 'z':
            event?.preventDefault();
            // Undo functionality could be implemented here
            break;
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [hasUnsavedChanges]);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Breadcrumb />
          
          {/* Page Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-foreground">
                Persönliche Verfügbarkeitsverwaltung
              </h1>
              <p className="text-muted-foreground mt-2">
                Verwalten Sie Ihre Arbeitszeiten und Verfügbarkeit effizient
              </p>
            </div>
            
            <div className="flex items-center space-x-3">
              <Button
                variant="outline"
                iconName="Calendar"
                iconPosition="left"
                iconSize={16}
                onClick={() => setShowVacationModal(true)}
              >
                Urlaub beantragen
              </Button>
              
              {hasUnsavedChanges && (
                <Button
                  variant="default"
                  iconName="Save"
                  iconPosition="left"
                  iconSize={16}
                  onClick={handleSaveChanges}
                >
                  Änderungen speichern
                </Button>
              )}
            </div>
          </div>

          {/* Week Navigator */}
          <WeekNavigator
            selectedWeek={selectedWeek}
            onWeekChange={handleWeekChange}
            onTodayClick={handleTodayClick}
          />

          {/* Main Content */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Sidebar */}
            <div className="lg:col-span-1">
              <AvailabilitySidebar
                onPresetApply={handlePresetApply}
                availabilityStats={availabilityStats}
                upcomingImpact={upcomingImpact}
                onPatternSave={handlePatternSave}
                savedPatterns={savedPatterns}
              />
            </div>

            {/* Main Grid */}
            <div className="lg:col-span-3">
              <AvailabilityGrid
                selectedWeek={selectedWeek}
                availability={availability}
                onAvailabilityChange={handleAvailabilityChange}
                onBulkUpdate={handleBulkUpdate}
                conflictingShifts={conflictingShifts}
              />
            </div>
          </div>

          {/* Unsaved Changes Warning */}
          {hasUnsavedChanges && (
            <div className="fixed bottom-6 right-6 bg-warning text-warning-foreground px-4 py-3 rounded-lg shadow-floating flex items-center space-x-3">
              <Icon name="AlertCircle" size={20} />
              <span className="text-sm font-medium">
                Sie haben ungespeicherte Änderungen
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleSaveChanges}
                className="text-warning-foreground hover:text-warning-foreground"
              >
                Speichern
              </Button>
            </div>
          )}
        </div>
      </main>

      {/* Modals */}
      <ConflictModal
        isOpen={showConflictModal}
        onClose={() => setShowConflictModal(false)}
        conflicts={currentConflicts}
        onResolveConflict={handleResolveConflict}
        onIgnoreConflict={handleIgnoreConflict}
      />

      <VacationRequestModal
        isOpen={showVacationModal}
        onClose={() => setShowVacationModal(false)}
        onSubmit={handleVacationSubmit}
        existingRequests={vacationRequests}
      />
    </div>
  );
};

export default PersonalAvailabilityManagement;